import { InputBase, styled } from '@mui/material'
import {
  ChangeEvent,
  FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDebounce } from '../../../../hooks/useDebounce'
import { colorPalette } from '../../../../style/colorPalette'

type TextInputProps = {
  value: string | undefined
  onChange: (v: string | undefined) => void
  onBlur?: (v: string | undefined) => void
  variant?: ReportInputVariant
  multiline?: boolean
  autoFocus?: boolean
  readOnly?: boolean
  placeholder?: string
}
export const TextInput = ({
  value,
  onChange,
  onBlur,
  variant,
  multiline,
  autoFocus,
  readOnly,
  placeholder,
}: TextInputProps) => {
  const [inputValue, setInputValue] = useState<string | undefined>(value)
  const [changedByInput, setChangedByInput] = useState<boolean>(false)
  const editedSinceFocusRef = useRef<boolean>(false)
  useEffect(() => {
    setInputValue(value)
    setChangedByInput(false)
  }, [value])
  const debouncedValue = useDebounce(inputValue, 300)
  useEffect(() => {
    if (changedByInput) {
      onChange && onChange(debouncedValue)
    }
  }, [changedByInput, debouncedValue, onChange])
  const onChangeInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    setChangedByInput(true)
    editedSinceFocusRef.current = true
  }, [])
  const onBlurInput = useCallback(
    (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (editedSinceFocusRef.current) {
        editedSinceFocusRef.current = false
        onBlur?.(e.target.value)
      }
    },
    [onBlur]
  )
  return (
    <StyledInput
      value={inputValue}
      onChange={onChangeInput}
      onBlur={onBlurInput}
      variant={variant}
      multiline={multiline}
      minRows={2}
      {...(!!variant && variant !== 'comment' && { maxRows: 2 })}
      autoFocus={autoFocus}
      readOnly={readOnly}
      placeholder={placeholder}
    />
  )
}

type ReportInputVariant = 'header' | 'leadSentence' | 'comment'
type StyledInputProps = {
  variant?: 'header' | 'leadSentence' | 'comment'
}
const StyledInput = styled(InputBase)(({
  variant = 'comment',
}: StyledInputProps) => {
  const fontStyle = useMemo(() => {
    switch (variant) {
      case 'header':
        return {
          fontSize: '18px !important',
          fontWeight: 600,
          lineHeight: 1,
          color: colorPalette.monotone[10],
        }
      case 'leadSentence':
        return {
          fontSize: '14px !important',
          color: colorPalette.monotone[9],
        }
      case 'comment':
        return {
          fontSize: '14px !important',
          color: colorPalette.monotone[9],
        }
    }
    return {}
  }, [variant])
  return {
    width: '100%',
    padding: 0,
    alignItems: variant === 'header' ? 'center' : 'flex-start',
    '& .MuiInputBase-input': { ...fontStyle, padding: 0 },
    '& .MuiInputBase-input::placeholder': {
      color: colorPalette.monotone[4],
      opacity: 1,
    },
  }
})
